import { useEffect, useRef } from 'react';
import useAuth from './useAuth';
import { Logger } from '../utils/logger';
import applicationSocketService, { ApplicationEvent } from '../sockets/applicationSocketService';

const logger = new Logger('useApplicationUpdates');

type ApplicationEventType = 'created' | 'updated' | 'deleted';

export const useApplicationUpdates = (
  onUpdateFn: (event: { application: ApplicationEvent['application']; type: ApplicationEventType }) => void,
  applicationId: string | null = null
): void => {
  const { user } = useAuth();
  const isMounted = useRef(true);
  const subscriptionRef = useRef(false);
  const onUpdate = useRef(onUpdateFn);

  useEffect(() => {
    onUpdate.current = onUpdateFn;
  }, [onUpdateFn]);

  useEffect(() => {
    isMounted.current = true;
    subscriptionRef.current = false;

    if (!user?.id) {
      logger.debug(`Skipping subscription: userId=${user?.id}`);
      return;
    }

    logger.debug(`Setting up subscription for applicationId: ${applicationId || 'all'}`);
    const subscribe = async () => {
      try {
        await applicationSocketService.subscribeTo(
          applicationId,
          { userId: user.id, namespace: 'application-updates' },
          {
            onCreated: (event) => {
              if (isMounted.current) {
                onUpdate.current({ application: event.application, type: 'created' });
              }
            },
            onUpdated: (event) => {
              if (isMounted.current) {
                onUpdate.current({ application: event.application, type: 'updated' });
              }
            },
            onDeleted: (event) => {
              if (isMounted.current) {
                onUpdate.current({ application: event.application, type: 'deleted' });
              }
            }
          }
        );
        subscriptionRef.current = true;
        logger.debug('Successfully subscribed to Application updates');
      } catch (error) {
        logger.error('Subscription failed:', error);
        subscriptionRef.current = false;
      }
    };

    subscribe();

    return () => {
      logger.debug(`Cleaning up subscription for applicationId: ${applicationId || 'all'}`);
      isMounted.current = false;

      if (user?.id) {
        applicationSocketService.unsubscribe(applicationId).catch((err) => logger.warn('Unsubscribe error:', err));
      }
      subscriptionRef.current = false;
    };
  }, [applicationId, user?.id]);
};
